import {
  ANNUALLY,
  MONTHLY,
  SelectableBillingPeriodTypes
} from '@constants/billingPeriods'
import { CurrencyType, PLN } from '@constants/currencies'
import { COMPANY, CustomerTypes } from '@constants/customerTypes'
import { PaymentMethodTypes } from '@constants/paymentMethods'
import { PlanRecordKeyTypes } from '@constants/plans'
import { useFeatureFlags } from '@contexts/featureFlags'
import { useUserContext } from '@contexts/user'
import { getCreditCardStepErrorMessage } from '@helpers/errors'
import {
  convertPriceToNetto,
  getActiveDiscountForPlanAndPeriod
} from '@helpers/payment'
import { PAYMENT_VIEWS, PaymentViewTypes } from '@pages/Checkout/constants'
import {
  getCustomerType,
  isCustomerDataFilled
} from '@pages/Checkout/helpers/billing'
import {
  getCheckoutData,
  setCheckoutData
} from '@pages/Checkout/helpers/checkoutData'
import { getPackagePrice } from '@pages/Checkout/helpers/getPackagePrice'
import { getValidationSchema } from '@pages/Checkout/helpers/getValidationSchema'
import {
  getFormattedExpirationDate,
  getPaymentMethod,
  isCreditCardDataFilled
} from '@pages/Checkout/helpers/payment'
import { useGetPaymentAction } from '@pages/Checkout/hooks/useGetPaymentAction'
import { PAYMENTS } from '@routes/path'
import { CheckoutRoutesTypes } from '@routes/path/payments'
import { saveCustomerResourceData } from '@services/checkout/saveCustomerResourceData'
import { updateCustomerResourceData } from '@services/checkout/updateCustomerResourceData'
import { Discount } from '@services/discounts'
import { useGetCalculatedExtensionDate } from '@services/payments/useGetCalculatedExtensionDate'
import {
  CountriesType,
  useGetCountries
} from '@services/payments/useGetCountries'
import {
  CUSTOMER_COMPANY_TYPE,
  CUSTOMER_INDIVIDUAL_TYPE,
  CustomerTypes as CustomerTypesResourceData,
  GetCustomerResourceResponse,
  useGetCustomerResource
} from '@services/payments/useGetCustomerResource'
import { useGetPackages } from '@services/payments/useGetPackages'
import {
  GetPaymentMethodResponse,
  useGetPaymentMethod
} from '@services/payments/useGetPaymentMethod'
import { emitToast } from '@ui-kit'
import { FormikErrors, FormikHelpers, useFormik } from 'formik'
import i18next from 'i18next'
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

const { DETAILS, FORM } = PAYMENT_VIEWS

export type CheckoutProviderProps = {
  children: ReactNode
}

export interface InitialValuesTypes {
  customerType: CustomerTypes
  country: string
  companyName: string
  nameAndSurname: string
  vat: string
  address: string
  postCode: string
  city: string
  paymentMethod: PaymentMethodTypes
  cardNumber: string
  cardExpiry: string
  cardCvv: string
}

export type FormikType = ReturnType<typeof useFormik<InitialValuesTypes>> & {
  errors: FormikErrors<InitialValuesTypes>
}

export type CheckoutProviderValue = {
  chosenBillingPeriod: SelectableBillingPeriodTypes
  setBillingPeriod: Dispatch<SetStateAction<SelectableBillingPeriodTypes>>
  isLastStep: boolean
  isFirstOrLastStep: boolean
  goToStep: (stepRoute: CheckoutRoutesTypes) => void
  goToPreviousStep: (stepRoute: CheckoutRoutesTypes) => void
  steps: Array<{ path: CheckoutRoutesTypes; label: string }>
  isCurrentPlan: (planRecordKey?: PlanRecordKeyTypes) => boolean
  accountCurrency: CurrencyType
  getPlanPrice: (
    planRecordKey: PlanRecordKeyTypes,
    billingPeriod?: SelectableBillingPeriodTypes
  ) => number
  packagesIsLoading: boolean
  getAnnualSavingPrice: (planRecordKey: PlanRecordKeyTypes) => number
  toggleBillingPeriod: () => void
  accountPlanRecordKey: PlanRecordKeyTypes
  accountPlanName: string
  checkoutDataInContext: {
    chosenRecordKey: PlanRecordKeyTypes | null
    currentStep: CheckoutRoutesTypes
  }
  setCheckoutDataInContext: Dispatch<
    SetStateAction<{
      chosenRecordKey: PlanRecordKeyTypes | null
      currentStep: CheckoutRoutesTypes
    }>
  >
  getActiveDiscountForPlan: (
    planRecordKey: PlanRecordKeyTypes
  ) => Discount | undefined
  countriesList: CountriesType[]
  customerResourceData: GetCustomerResourceResponse
  billingInformationIsLoading: boolean
  formik: FormikType
  chosenPlanName: string
  paymentMethodsData: GetPaymentMethodResponse
  paymentMethodsIsLoading: boolean
  customerDataView: PaymentViewTypes | null
  setCustomerDataView: Dispatch<SetStateAction<PaymentViewTypes | null>>
  paymentMethodView: PaymentViewTypes | null
  setPaymentMethodView: Dispatch<SetStateAction<PaymentViewTypes | null>>
  isFormFilled: boolean
  calculatedExtensionDate: string | undefined
  isCreditCardDataAvailable: boolean
}

const CheckoutContext = createContext<CheckoutProviderValue | null>(null)

export const CheckoutProvider: FC<CheckoutProviderProps> = ({ children }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const hasAccessToCheckoutIterationV2 = useFeatureFlags('SPA_CHECKOUT_V2')

  const {
    billingPeriod: accountPeriod,
    planRecordKey: accountPlanRecordKey,
    planName: accountPlanName,
    accountCurrency,
    discounts,
    country: accountCountry
  } = useUserContext()

  const steps = useMemo(
    () => [
      {
        path: PAYMENTS.CHECKOUT.PLANS,
        label: t('checkout.wrapper.steps.plans')
      },
      {
        path: PAYMENTS.CHECKOUT.CONFIGURE,
        label: t('checkout.wrapper.steps.configure')
      },
      {
        path: PAYMENTS.CHECKOUT.PAYMENT,
        label: t('checkout.wrapper.steps.payment')
      },
      {
        path: PAYMENTS.CHECKOUT.CONFIRMATION,
        label: t('checkout.wrapper.steps.confirmation')
      }
    ],
    [t]
  )

  const [chosenBillingPeriod, setBillingPeriod] =
    useState<SelectableBillingPeriodTypes>(
      accountPeriod === ANNUALLY ? ANNUALLY : MONTHLY
    )

  const toggleBillingPeriod = useCallback(() => {
    setBillingPeriod(prev => (prev === ANNUALLY ? MONTHLY : ANNUALLY))
  }, [])

  const [checkoutDataInContext, setCheckoutDataInContext] = useState({
    chosenRecordKey: getCheckoutData().selectedRecordKey,
    currentStep: getCheckoutData().currentStep || PAYMENTS.CHECKOUT.PLANS
  })

  useEffect(() => {
    const { selectedRecordKey, currentStep } = getCheckoutData()

    if (!selectedRecordKey) {
      setCheckoutData({})
      navigate(PAYMENTS.CHECKOUT.PLANS, { replace: true })

      return
    }

    const matchedStep = steps.find(step => step.path === location.pathname)

    if (!matchedStep) {
      navigate(PAYMENTS.CHECKOUT.PLANS, { replace: true })
    } else {
      const currentStepIndex = steps.findIndex(
        step => step.path === location.pathname
      )

      const storedStepIndex = steps.findIndex(step => step.path === currentStep)

      if (storedStepIndex >= currentStepIndex || !currentStep) {
        setCheckoutData({
          currentStep: matchedStep.path
        })

        setCheckoutDataInContext(prev => ({
          ...prev,
          currentStep: matchedStep.path
        }))
      }
    }
  }, [location.pathname, steps, navigate])

  const { data: packages, isLoading: packagesIsLoading } = useGetPackages()

  const getChosenPlanName = useCallback(() => {
    if (!checkoutDataInContext.chosenRecordKey || !packages) {
      return 'Cannot find plan name'
    }

    const plan = packages?.find(({ record_key }) => {
      return record_key === checkoutDataInContext.chosenRecordKey
    })

    return plan ? plan.name : 'Cannot find plan name'
  }, [packages, checkoutDataInContext.chosenRecordKey])

  const chosenPlanName = getChosenPlanName()

  const getPlanPrice = useCallback(
    (
      planRecordKey: PlanRecordKeyTypes,
      billingPeriod: SelectableBillingPeriodTypes = chosenBillingPeriod
    ) => {
      const getGrossPrice = getPackagePrice({
        packages: packages ?? [],
        periodToFind: billingPeriod,
        currencyToFind: accountCurrency,
        packageRecordKey: planRecordKey
      })

      return accountCurrency === PLN
        ? convertPriceToNetto(getGrossPrice)
        : getGrossPrice
    },
    [packages, chosenBillingPeriod, accountCurrency]
  )

  const getAnnualSavingPrice = useCallback(
    (planRecordKey: PlanRecordKeyTypes) => {
      const priceMonthly = getPlanPrice(planRecordKey, MONTHLY)

      const priceAnually = getPlanPrice(planRecordKey, ANNUALLY)

      return (priceMonthly - priceAnually) * 12
    },
    [getPlanPrice]
  )

  const isCurrentPlan = useCallback(
    (planRecordKey?: PlanRecordKeyTypes) =>
      planRecordKey === accountPlanRecordKey,
    [accountPlanRecordKey]
  )

  const getActiveDiscountForPlan = useCallback(
    (planRecordKey: string) =>
      getActiveDiscountForPlanAndPeriod(
        discounts,
        planRecordKey,
        chosenBillingPeriod
      ),
    [discounts, chosenBillingPeriod]
  )

  const isLastStep = location.pathname === PAYMENTS.CHECKOUT.CONFIRMATION
  const isFirstStep = location.pathname === PAYMENTS.CHECKOUT.PLANS
  const isFirstOrLastStep = isFirstStep || isLastStep

  const goToStep = useCallback(
    (stepRoute: CheckoutRoutesTypes) => {
      const { selectedRecordKey } = getCheckoutData()

      if (!selectedRecordKey) {
        setCheckoutData({})
      }

      navigate(stepRoute)

      setCheckoutData({
        currentStep: stepRoute
      })

      setCheckoutDataInContext(prev => ({
        ...prev,
        currentStep: stepRoute
      }))
    },
    [navigate]
  )

  const goToPreviousStep = useCallback(
    (stepRoute: CheckoutRoutesTypes) => {
      let newStep: CheckoutRoutesTypes = PAYMENTS.CHECKOUT.PLANS

      switch (stepRoute) {
        case PAYMENTS.CHECKOUT.CONFIGURE:
          newStep = PAYMENTS.CHECKOUT.PLANS
          break
        case PAYMENTS.CHECKOUT.PAYMENT:
          newStep = PAYMENTS.CHECKOUT.CONFIGURE
          break
        default:
          newStep = PAYMENTS.CHECKOUT.PLANS
      }

      navigate(newStep)

      setCheckoutData({
        currentStep: newStep
      })

      setCheckoutDataInContext(prev => ({
        ...prev,
        currentStep: newStep
      }))
    },
    [navigate]
  )

  const changeStep = (step: CheckoutRoutesTypes) => {
    setCheckoutData({
      currentStep: step
    })

    setCheckoutDataInContext(prev => ({
      ...prev,
      currentStep: step
    }))
  }

  const { data: calculatedExtensionDate } =
    useGetCalculatedExtensionDate(chosenBillingPeriod)

  const { data: customerResourceData, isLoading: customerResourceIsLoading } =
    useGetCustomerResource()

  const { data: paymentMethodsData, isLoading: paymentMethodsIsLoading } =
    useGetPaymentMethod()

  const { data: countriesData, isLoading: countriesIsLoading } =
    useGetCountries(i18next.language)

  const isCustomerDataAvailable =
    !!customerResourceData && isCustomerDataFilled(customerResourceData)

  const isCreditCardDataAvailable =
    !!hasAccessToCheckoutIterationV2 &&
    !!paymentMethodsData &&
    isCreditCardDataFilled(paymentMethodsData)

  const countriesList = useMemo(
    () => countriesData?.countries || [],
    [countriesData]
  )

  const billingInformationIsLoading =
    customerResourceIsLoading || countriesIsLoading

  const initialValues: InitialValuesTypes = {
    customerType: getCustomerType(customerResourceData?.is_company),
    country: customerResourceData?.country || accountCountry || 'PL',
    companyName: customerResourceData?.name || '',
    nameAndSurname: customerResourceData?.name || '',
    vat: customerResourceData?.tax_identifier || '',
    address: customerResourceData?.street || '',
    postCode: customerResourceData?.postal_code || '',
    city: customerResourceData?.city || '',
    paymentMethod: getPaymentMethod(paymentMethodsData?.type),
    cardNumber: '',
    cardExpiry: isCreditCardDataAvailable
      ? getFormattedExpirationDate(paymentMethodsData.card_info.expiration_date)
      : '',
    cardCvv: ''
  }

  const defaultCustomerDataView = isCustomerDataAvailable ? DETAILS : FORM
  const defaultPaymentMethodView = isCreditCardDataAvailable ? DETAILS : FORM

  const [customerDataView, setCustomerDataView] =
    useState<PaymentViewTypes | null>(null)

  const [paymentMethodView, setPaymentMethodView] =
    useState<PaymentViewTypes | null>(null)

  useEffect(() => {
    if (!customerResourceIsLoading) {
      setCustomerDataView(defaultCustomerDataView)
    }

    if (!paymentMethodsIsLoading) {
      setPaymentMethodView(defaultPaymentMethodView)
    }
  }, [
    defaultCustomerDataView,
    customerResourceIsLoading,
    defaultPaymentMethodView,
    paymentMethodsIsLoading
  ])

  const paymentAction = useGetPaymentAction({
    goToStep,
    chosenBillingPeriod,
    changeStep
  })

  const isFormFilled = isCustomerDataAvailable && isCreditCardDataAvailable

  const onSubmit = async (
    values: InitialValuesTypes,
    { setFieldError }: FormikHelpers<InitialValuesTypes>
  ) => {
    const isCompany = values.customerType === COMPANY

    const name = isCompany ? values.companyName : values.nameAndSurname

    const taxIdentifier = isCompany ? { tax_identifier: values.vat } : {}

    const isCompanyCustomer: { is_company: CustomerTypesResourceData } =
      isCompany
        ? { is_company: CUSTOMER_COMPANY_TYPE }
        : { is_company: CUSTOMER_INDIVIDUAL_TYPE }

    try {
      if (isCustomerDataAvailable) {
        await updateCustomerResourceData({
          name,
          street: values.address,
          city: values.city,
          country: values.country,
          postal_code: values.postCode,
          ...isCompanyCustomer,
          ...taxIdentifier
        })
      } else {
        await saveCustomerResourceData({
          name,
          street: values.address,
          city: values.city,
          country: values.country,
          postal_code: values.postCode,
          ...isCompanyCustomer,
          ...taxIdentifier
        })
      }

      const paymentActionFunction = paymentAction[values.paymentMethod]

      await paymentActionFunction({
        cardNumber: values.cardNumber,
        cardExpiry: values.cardExpiry,
        cardCvv: values.cardCvv
      })
    } catch (error) {
      emitToast({
        type: 'error',
        message: t(
          getCreditCardStepErrorMessage(error, values.country, setFieldError)
        )
      })
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema({
      countriesList,
      isPaymentMethodForm: paymentMethodView === FORM
    }),
    enableReinitialize: true,
    onSubmit
  })

  const value = useMemo(
    () => ({
      chosenBillingPeriod,
      setBillingPeriod,
      isLastStep,
      isCurrentPlan,
      accountCurrency,
      getPlanPrice,
      packagesIsLoading,
      getAnnualSavingPrice,
      isFirstOrLastStep,
      goToStep,
      goToPreviousStep,
      steps,
      toggleBillingPeriod,
      accountPlanRecordKey: accountPlanRecordKey as PlanRecordKeyTypes,
      accountPlanName: accountPlanName as string,
      checkoutDataInContext,
      setCheckoutDataInContext,
      getActiveDiscountForPlan,
      countriesList,
      customerResourceData:
        customerResourceData || ({} as GetCustomerResourceResponse),
      billingInformationIsLoading,
      formik,
      chosenPlanName,
      paymentMethodsData:
        paymentMethodsData || ({} as GetPaymentMethodResponse),
      paymentMethodsIsLoading,
      customerDataView,
      setCustomerDataView,
      paymentMethodView,
      setPaymentMethodView,
      isFormFilled,
      calculatedExtensionDate: calculatedExtensionDate?.nextPaymentDate,
      isCreditCardDataAvailable
    }),
    [
      chosenBillingPeriod,
      setBillingPeriod,
      isLastStep,
      isCurrentPlan,
      accountCurrency,
      getPlanPrice,
      packagesIsLoading,
      getAnnualSavingPrice,
      isFirstOrLastStep,
      goToStep,
      goToPreviousStep,
      steps,
      toggleBillingPeriod,
      accountPlanRecordKey,
      accountPlanName,
      checkoutDataInContext,
      setCheckoutDataInContext,
      getActiveDiscountForPlan,
      countriesList,
      customerResourceData,
      billingInformationIsLoading,
      formik,
      chosenPlanName,
      paymentMethodsData,
      paymentMethodsIsLoading,
      customerDataView,
      setCustomerDataView,
      paymentMethodView,
      setPaymentMethodView,
      isFormFilled,
      calculatedExtensionDate,
      isCreditCardDataAvailable
    ]
  )

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  )
}

export const useCheckoutContext = () => {
  const value = useContext(CheckoutContext)

  if (!value) {
    throw new Error('useCheckoutContext must be used inside CheckoutProvider')
  }

  return value
}
